import React, { FC, useState, useEffect } from 'react';
import _ from 'lodash';

import { useI18n, useSelector } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { HeaderBox, ModalWrapper } from './styles';
import ClaimDevice from './Components/ClaimDevice';
import ClaimDeviceV2 from '~Components/ClaimDevice';
import IZettleSettings from './Components/IZettleSettings';
import { Redirect } from 'react-router';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useQuery } from '@apollo/client';
import { GET_LAYOUTS } from './api/queries';
import Icon from '~ComponentsImproved/Icon';
import CheckIcon from '~Assets/icons/check-circle.svg';
import SelectLayout from './Components/SelectLayout';
import { useStorage } from '~Components/Storage';

enum STEP {
  REGISTER = 'REGISTER', // Claim device (there is no device claimed yet)
  IZETTLE = 'IZETTLE', // Zettle settings
  LAYOUT = 'LAYOUT', // POS Layout
  COMPLETE = 'COMPLETE', // Setup complete, wait for 1,5 seconds then redirect to gateway
  REDIRECT = 'REDIRECT',
}

interface Props {
  isHandy?: boolean;
}

const Onboarding: FC<Props> = ({ isHandy }) => {
  const { i18n } = useI18n();
  const deviceHandler = useDeviceHandler();

  const venueId = useSelector((state) => state.venueId) as string;
  const companyFlags = useSelector((state) => state.companyFlags);
  const [storage, updateStorage, storageLoading] = useStorage();

  // Ensure we have set POS Layout for this device
  const POSLayout = useSelector((state) => state.POSLayout);

  const layoutsQuery = useQuery(GET_LAYOUTS, {
    variables: { venueId },
    skip: !venueId,
  });

  const POSLayouts = layoutsQuery.data?.venue?.company?.layouts ?? [];

  const missingTaxComplianceInfo =
    !storageLoading && (!storage.taxComplianceDeviceId || !storage.taxComplianceDeviceName);

  /* Check register completion */
  const shouldShowRegister = companyFlags.useNewLicenses
    ? !deviceHandler.activeDevice || missingTaxComplianceInfo
    : missingTaxComplianceInfo;

  const [step, setStep] = useState<STEP | null>(shouldShowRegister ? STEP.REGISTER : null);

  /** On complete of the Onboarding */
  useEffect(() => {
    if (step !== STEP.COMPLETE) {
      return;
    }

    setTimeout(() => {
      setStep(STEP.REDIRECT);
    }, 1500);
  }, [step]);

  return (
    <Modal
      noPadding={isHandy ? true : false}
      isShowing={Boolean(step) && step !== STEP.REDIRECT}
      backdropClose={false}>
      <ModalWrapper
        display="flex"
        flexDirection="column"
        gap="medium"
        alignItems="center"
        justifyContent="center"
        padding={isHandy ? 'small' : '3xl'}
        marginTop="small"
        marginBottom="small">
        {step === STEP.REGISTER &&
          (companyFlags.useNewLicenses ? (
            <ClaimDeviceV2 isHandy={isHandy} onComplete={() => setStep(STEP.IZETTLE)} />
          ) : (
            <ClaimDevice isHandy={isHandy} onComplete={() => setStep(STEP.IZETTLE)} />
          ))}

        {step === STEP.IZETTLE && (
          <IZettleSettings
            isHandy={isHandy}
            onSkipClick={() =>
              !POSLayout && POSLayouts.length > 0 ? setStep(STEP.LAYOUT) : setStep(STEP.COMPLETE)
            }
          />
        )}

        {step === STEP.LAYOUT && (
          <SelectLayout isHandy={isHandy} onSkipClick={() => setStep(STEP.COMPLETE)} />
        )}

        {step === STEP.COMPLETE && (
          <HeaderBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="xs"
            justifyContent="center">
            <Icon
              height={isHandy ? '48px' : '64px'}
              width={isHandy ? '48px' : '64px'}
              src={CheckIcon}
              color="var(--colorUIGreen)"
            />
            <Heading level={isHandy ? 3 : 1} color="var(--colorWhite)">
              {i18n('Onboarding.SetupComplete')}
            </Heading>
            <Paragraph level={isHandy ? 5 : 2} color="var(--colorBlueGray200)">
              {i18n('Onboarding.SetupComplete.Text')}
            </Paragraph>
          </HeaderBox>
        )}

        {step === STEP.REDIRECT && <Redirect to="gateway" />}
      </ModalWrapper>
    </Modal>
  );
};

export default Onboarding;
